// src/stores/useBasemapStore.js
//
// User-selectable "classic" Mapbox raster basemaps. Persisted per-device
// (see auth/persistKeys.js) so the choice survives a reload but isn't
// wiped on a different-user login — it's a display preference, not tied
// to identity or entitlements.

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getConfig } from "../config";
import { PERSIST_KEYS } from "../auth/persistKeys";

export const CLASSIC_BASEMAPS = [
  { id: "satellite-streets-v11", label: "Satellite Streets" },
  { id: "satellite-v9", label: "Satellite" },
  { id: "streets-v11", label: "Streets" },
  { id: "outdoors-v11", label: "Outdoors" },
  { id: "light-v10", label: "Light" },
  { id: "dark-v10", label: "Dark" },
];

const DEFAULT_BASEMAP_ID = CLASSIC_BASEMAPS[0].id;

// PadPlanner's maps offer the same choices with the current Satellite Streets
// (v12) in place of v11, and keep their own selection: Explore's list and
// default are unchanged, and a choice made in Explore does not move
// PadPlanner off v12. Both selections persist under the same key.
export const PADPLANNER_BASEMAPS = [
  { id: "satellite-streets-v12", label: "Satellite Streets" },
  ...CLASSIC_BASEMAPS.filter((b) => b.id !== "satellite-streets-v11"),
];

const PADPLANNER_DEFAULT_BASEMAP_ID = PADPLANNER_BASEMAPS[0].id;

/** size: 256 or 512, which must match the raster source's tileSize. */
export function basemapTileUrl(styleId, size = 256) {
  const id = [...CLASSIC_BASEMAPS, ...PADPLANNER_BASEMAPS].some((b) => b.id === styleId)
    ? styleId
    : DEFAULT_BASEMAP_ID;
  return `https://api.mapbox.com/styles/v1/mapbox/${id}/tiles/${size}/{z}/{x}/{y}@2x?access_token=${getConfig().mapboxToken}`;
}

export const useBasemapStore = create(
  persist(
    (set) => ({
      activeBasemapId: DEFAULT_BASEMAP_ID,
      setBasemap: (id) => set({ activeBasemapId: id }),
      padplannerBasemapId: PADPLANNER_DEFAULT_BASEMAP_ID,
      setPadplannerBasemap: (id) => set({ padplannerBasemapId: id }),
    }),
    {
      name: PERSIST_KEYS.basemap,
    }
  )
);
